{
  "common": {
    "cancel": "Cancel",
    "create": "Create",
    "creating": "Creating…",
    "retry": "Try again",
    "readOnlyBanner": "This test workspace is read-only. Everything here stays open to read — past runs, their results and the whole configuration — but new work and edits are off.",
    "readOnlyReason": "This test workspace is read-only, so this cannot be changed.",
    "noWriteBanner": "You can read everything in this test workspace — data, cases, past runs and results — but edits, uploads and runs need the Collaborator permission on this model. The model’s owner or an administrator can grant it.",
    "noWriteReason": "Changing this test workspace needs the Collaborator permission on this model.",
    "stillWorking": "Still working…"
  },
  "home": {
    "title": "Test Ground",
    "subtitle": "Build and run test pipelines against your data.",
    "workspacesHeading": "Test workspaces",
    "newWorkspace": "New workspace",
    "empty": "No test workspaces yet.",
    "createFirst": "Create your first workspace",
    "cardMetaTables_one": "{{count}} table",
    "cardMetaTables_other": "{{count}} tables",
    "cardMetaCases_one": "{{count}} test case",
    "cardMetaCases_other": "{{count}} test cases",
    "open": "Open",
    "delete": "Delete",
    "deleteTitle": "Delete workspace?",
    "deleteBody": "This permanently deletes \"{{name}}\" and all of its data, test cases, and runs.",
    "backToList": "Back to workspaces",
    "nameLabel": "Workspace name",
    "namePlaceholder": "e.g. Annual PD validation",
    "scopeLabel": "Vertical",
    "scopeChange": "Change",
    "scopeGenericNotAllowed": "Generic Task can't be used for a test workspace — choose a vertical from the menu.",
    "scopeHint": "The methodology scope for this workspace. This cannot be changed after the workspace is created.",
    "outputLanguage": "Response language"
  },
  "rail": {
    "data": "Data",
    "dataCount": "{{confirmed}}/{{total}} confirmed",
    "testCases": "Test cases",
    "newCase": "New test case",
    "confirmFirst": "Confirm at least one table to add a test case.",
    "noCases": "No test cases yet.",
    "caseNameLabel": "Test case name",
    "caseNamePlaceholder": "e.g. Baseline run",
    "pickCase": "Select a test case to continue."
  },
  "case": {
    "saveWebhook": "Save",
    "savingWebhook": "Saving…",
    "webhookHint": "Runs you start from now on will send their result to this https address when they finish. Leave it empty to send nothing.",
    "webhookLabel": "Webhook URL",
    "webhookPlaceholder": "https://example.com/hooks/reggie"
  },
  "stage": {
    "data": {
      "tab": "Data"
    },
    "discover": {
      "tab": "Discover"
    },
    "build": {
      "tab": "Build"
    },
    "run": {
      "tab": "Run"
    }
  },
  "discover": {
    "openingTitle": "What are we trying to do today?",
    "openingSubtitle": "Describe your goal. I'll retrieve the right methodology and functions and propose a pipeline against your confirmed data.",
    "composerPlaceholder": "Describe what you want to test…",
    "composerBusy": "Waiting for the current response…",
    "composerBusyHint": "One message at a time — waiting for the assistant to finish.",
    "send": "Send",
    "thinking": "Working…",
    "cancel": "Stop",
    "turnFailed": "The assistant couldn't complete that. Please try again.",
    "failure": {
      "agent_max_rounds": "The assistant ran out of steps before finishing. Try a narrower request.",
      "agent_timeout": "The assistant took too long to respond. Please try again.",
      "agent_refused": "The assistant declined this request. Try rephrasing it.",
      "llm_infrastructure": "The assistant is temporarily unavailable. Please try again shortly.",
      "tool_failed": "The assistant couldn't reach the data it needed. Please try again.",
      "tool_result_shape_invalid": "The assistant received data it couldn't read. Please try again; if it keeps happening, contact support.",
      "discovery_empty_answer": "The assistant didn't produce a reply. Please try again.",
      "discovery_incomplete": "The assistant's reply was cut off. Please try again.",
      "config_generation_failed": "The pipeline was saved, but its settings couldn't be filled in. Try again to complete them.",
      "config_incomplete": "The pipeline was saved, but its settings were only partly filled in. Try again to complete them.",
      "config_draft_missing": "Describe a pipeline first, then its settings can be filled in.",
      "node_already_resolved": "This block already has code. Choose “Regenerate the code” to replace it."
    },
    "turnCancelled": "Stopped.",
    "noAnswer": "No response was produced.",
    "activityLines_other": "{{count}} retrieval steps",
    "activityLines_one": "{{count}} retrieval step",
    "proposalTitle": "Pipeline proposal",
    "proposalViewInBuild": "View in Build",
    "attachTooltip": "Attach a supporting document",
    "attachedChip": "📎 {{filename}}",
    "extracting": "Reading document…",
    "enqueued": "Message sent. Waiting for the assistant…",
    "micStart": "Start voice input",
    "micStop": "Finish voice input",
    "micCancel": "Cancel voice input",
    "micRequestingPermission": "Allow microphone access…",
    "micListening": "Listening…",
    "micSpeechDetected": "Listening to your message…",
    "micTranscribing": "Transcribing…",
    "micDone": "Voice message sent",
    "micLevel": "Microphone level",
    "micUnsupported": "Voice input is not supported in this browser."
  },
  "build": {
    "emptyTitle": "No pipeline yet",
    "emptySubtitle": "Design a pipeline in the Discover chat — once the assistant proposes one it appears here to review and refine.",
    "unboundInput": "Unbound input",
    "placeholder": "needs code",
    "portArgs": "inputs",
    "packageMembers": "outputs",
    "kind": {
      "input": "Data input",
      "bf": "Function",
      "custom": "Custom node"
    },
    "validationHeading": "Validation",
    "issueGoToNode": "{{issue}} — open this block",
    "issueGoToEdge": "{{issue}} — open this connection",
    "issuePipelineLevelHeading": "Whole pipeline",
    "valid": "Valid",
    "invalid_other": "{{count}} issues",
    "validHint": "This pipeline is valid and ready to run.",
    "paletteHeading": "Add",
    "addCustomNode": "Custom node",
    "paletteHint": "Base functions are added by the assistant in the Discover chat.",
    "lockedBanner": "The assistant is updating this pipeline. Editing is paused until it finishes.",
    "invalid_one": "{{count}} issue",
    "paletteFunctions": "Functions",
    "paletteSearch": "Search functions…",
    "paletteEmpty": "No functions match.",
    "addFunction": "Add to pipeline",
    "autoArrange": "Auto-arrange",
    "paletteDragHint": "Drag a function onto the canvas.",
    "dockedChatTitle": "Assistant",
    "expandChat": "Show assistant",
    "collapseChat": "Hide assistant",
    "resizeChat": "Resize assistant panel",
    "heldInvalidBanner": "This change isn't saved yet — resolve the highlighted issues to save it.",
    "invalidDraftBanner": "This saved pipeline has unresolved issues — fix them before it can run.",
    "paletteUncategorized": "Other",
    "editor": {
      "close": "Close",
      "inputMappings": "Connections",
      "noInputSlots": "This block takes no inputs.",
      "noIncomingEdges": "No connections into this block yet.",
      "editMapping": "Edit mapping",
      "emptyMapping": "No inputs mapped yet.",
      "parameters": "Parameters",
      "noEditableParams": "This function has no editable parameters.",
      "functionNotInCatalog": "This function is no longer in this workspace's catalog.",
      "missingWidgetKind": "“{{name}}” can’t be edited here. Contact support if you need to change it.",
      "pickColumn": "Pick a column produced by the connected upstream block.",
      "columnFreeText": "Type a column name — connect the input to pick from upstream columns.",
      "enumFreeText": "No options declared — enter a value.",
      "unset": "Not set",
      "defaultPlaceholder": "Default: {{value}}",
      "effectiveDefault": "Left blank — the schema default {{value}} is what will run.",
      "deleteNode": "Delete block",
      "editCustomCode": "Regenerate the code",
      "subKeyCompleteness": "{{filled}} of {{total}} required fields set"
    },
    "mapping": {
      "title": "Connection mapping",
      "unmapped": "Not mapped",
      "incompatible": "Type mismatch — the validator will flag this.",
      "save": "Save mapping",
      "deleteEdge": "Remove connection",
      "producerUnknown": "This block's outputs aren't in the catalog yet, so its members can't be listed here.",
      "consumerUnknown": "This block's inputs aren't declared yet, so its arguments can't be listed here.",
      "noArgs": "This block takes no inputs."
    },
    "validWithWarnings_one": "Valid — {{count}} to finish",
    "validWithWarnings_other": "Valid — {{count}} to finish",
    "configPending": "Configuration pending",
    "configPendingBanner": "Structure saved — configuration is incomplete, so this pipeline can't run yet.",
    "configPassRun": "Run configuration pass",
    "configPassStarted": "Started — follow it in the chat",
    "configPassRequest": "Run the configuration pass on the current draft."
  },
  "customNode": {
    "titleResolve": "Resolve custom node",
    "subtitle": "Describe the logic and let the assistant write the node, or upload existing code to adapt.",
    "turnActive": "The assistant is busy. Try again once the current response finishes.",
    "promptLabel": "What should this node do?",
    "promptPlaceholder": "e.g. Compute the population stability index between the reference and current score distributions.",
    "chooseFile": "Upload code",
    "uploadAction": "Upload & normalize",
    "generateAction": "Generate code",
    "forNode": "for node {{id}}",
    "forNew": "as a new step",
    "generateRequest": "Please generate a custom node {{target}}:",
    "uploadRequest": "Please normalize this uploaded code into a custom node {{target}} (file: {{name}}):"
  },
  "run": {
    "heading": "Run",
    "runButton": "Run pipeline",
    "running": "Running…",
    "cancel": "Cancel run",
    "versionLabel": "Run v{{n}}",
    "pastRunView": "Viewing past run",
    "emptyHint": "Run this pipeline to see execution status and results here.",
    "inFlight": "Running…",
    "blockProgress": "{{done}}/{{total}} steps",
    "failedGeneric": "The run failed. Review the pipeline and try again.",
    "cancelledHint": "This run was cancelled.",
    "resultsHeading": "Results",
    "sendToProject": "Send to project",
    "sendToProjectToolLabel": "Test Ground run",
    "noPreview": "No preview is available for this artifact.",
    "previewTruncated": "Showing the first {{shown}} of {{total}} rows.",
    "previewFirstRows": "Showing the first {{shown}} rows.",
    "artifacts": "Artifacts",
    "artifact": "Artifact",
    "download": "Download",
    "pastRuns": "Past runs",
    "timelineHeading": "Execution steps",
    "noArtifacts": "This run produced no artifacts.",
    "preview": "Preview",
    "hidePreview": "Hide preview",
    "block": {
      "status": {
        "succeeded": "Succeeded",
        "failed": "Failed",
        "skipped": "Skipped",
        "running": "Running",
        "pending": "Pending",
        "unknown": "Unknown"
      },
      "pendingSummary_one": "{{count}} more step pending",
      "pendingSummary_other": "{{count}} more steps pending",
      "outputs": "Outputs",
      "noOutputs": "This step produced no outputs.",
      "skippedUpstream": "An earlier step failed, so this step did not run.",
      "skippedCancelled": "The run was cancelled before this step ran.",
      "error": {
        "generic": "This step failed.",
        "validation_failed": "This step's configuration failed validation.",
        "unknown_param": "This step was given a parameter it does not recognize.",
        "unresolved_input_binding": "This step's input could not be connected to an earlier step's output.",
        "input_load_failed": "This step's input data could not be loaded.",
        "bf_invocation_failed": "This step's function failed while running.",
        "invalid_return": "This step's function returned an unexpected result.",
        "artifact_write_failed": "This step's output could not be saved.",
        "kernel_unreachable": "The execution engine could not be reached.",
        "block_timeout": "This step took too long and timed out.",
        "skipped_upstream_failed": "An earlier step failed, so this step did not run.",
        "cancelled": "The run was cancelled before this step finished.",
        "user_cancelled": "The run was cancelled before this step ran."
      }
    },
    "status": {
      "queued": "Queued",
      "executing": "Running",
      "running": "Running",
      "succeeded": "Succeeded",
      "failed": "Failed",
      "cancelled": "Cancelled",
      "stale": "Stopped"
    },
    "error": {
      "validation_failed": "This pipeline didn't pass validation. Fix the highlighted steps and run again.",
      "bf_invocation_failed": "A step's function failed while running. Review the highlighted step and try again.",
      "input_load_failed": "A step couldn't load its input data. Check your data and try again.",
      "unresolved_input_binding": "A step's input isn't connected to an earlier step's output. Check the connections and try again.",
      "block_timeout": "A step took too long and timed out. Please try again.",
      "kernel_unreachable": "The execution engine couldn't be reached. Please try again.",
      "invalid_return": "A step returned an unexpected result. Review the pipeline and try again.",
      "artifact_write_failed": "A step's output couldn't be saved. Please try again.",
      "unknown_param": "A step was given a setting it doesn't recognize. Review the step's configuration and try again.",
      "node_timeout": "A step ran past the time allowed for it and was stopped. Run it again to continue from this step — earlier steps keep their results.",
      "kernel_unresponsive": "The execution engine stopped responding while a step was running. Run it again to continue from that step — earlier steps keep their results.",
      "watchdog_stranded": "The run stopped responding and couldn't be recovered. Please run it again."
    }
  },
  "data": {
    "dropzoneTitle": "Add your data",
    "dropzoneSubtitle": "Drag and drop, or choose .parquet, .csv, or .xlsx files.",
    "chooseFiles": "Choose files",
    "addData": "Add data",
    "catalogHeading": "{{confirmed}}/{{total}} confirmed",
    "pollingAria": "Processing tables",
    "singleTable": "Table",
    "selectTable": "Select a table to view its details.",
    "uploadQueued": "Upload received — preparing your data.",
    "rowColCount": "{{rows}} rows · {{cols}} columns",
    "reEnrich": "Re-analyze",
    "confirm": "Confirm",
    "attentionTitle": "This table needs attention",
    "attentionGeneric": "Something went wrong while preparing this table. Try re-analyzing it.",
    "processingHint": "Preparing this table…",
    "confirmedHint": "This table is confirmed and ready to use in a test case.",
    "descriptionLabel": "Description",
    "descriptionPlaceholder": "What does this table represent?",
    "schemaTab": "Schema",
    "sampleTab": "Sample",
    "colName": "Column",
    "colType": "Type",
    "colRole": "Role",
    "colMeaning": "Meaning",
    "roleUnset": "Not set",
    "noColumns": "No columns detected.",
    "notDescribed": "Not described",
    "saveOverrides": "Save changes",
    "noSample": "No sample rows available."
  },
  "role": {
    "identifier": "Identifier",
    "time_index": "Time index",
    "target": "Target",
    "feature": "Feature",
    "reference": "Reference",
    "other": "Other"
  },
  "status": {
    "queuedEnumerate": "Queued",
    "enumerating": "Reading file",
    "queuedParse": "Queued",
    "parsing": "Converting",
    "queuedEnrich": "Queued",
    "enriching": "Analyzing",
    "ready": "Ready",
    "attention": "Needs attention",
    "confirmed": "Confirmed",
    "cancelled": "Cancelled",
    "deleted": "Deleted",
    "unknown": "Unknown"
  },
  "validation": {
    "generic": "This step has a configuration issue.",
    "input_table_unbound": "This input isn't bound to a confirmed table yet.",
    "custom_node_unresolved": "This custom node still needs its code.",
    "custom_io_undeclared": "This custom node hasn't declared its inputs and outputs.",
    "slot_name_invalid": "A custom node's input or output name can't be used. Use a name without slashes, and not just dots.",
    "unmapped_required_arg": "A required input isn't connected.",
    "unknown_output_member": "A connection references an output this step doesn't produce.",
    "unknown_input_arg": "A connection targets an input this step doesn't have.",
    "duplicate_arg_binding": "An input is connected more than once.",
    "duplicate_edge": "Two steps are connected more than once — combine the connection.",
    "forward_anchor_invalid": "This custom step doesn't clearly declare which input it forwards.",
    "custom_contract_missing": "This custom step's inputs and outputs couldn't be checked.",
    "custom_contract_divergent": "This custom step's inputs and outputs changed after its code was generated.",
    "missing_input_node": "This step needs an input that isn't connected.",
    "cycle_detected": "The pipeline has a loop between steps.",
    "disconnected_node": "This step isn't connected to the pipeline.",
    "content_type_mismatch": "A connection joins incompatible data types.",
    "column_unsatisfied": "A required column isn't available at this step.",
    "params_unfilled": "This step's parameters haven't been configured yet.",
    "missing_required_param": "A required parameter isn't set.",
    "incomplete_object_param": "A grouped parameter is missing required fields.",
    "null_param_value": "A parameter is set to no value, but this step needs one.",
    "param_type_mismatch": "A parameter has the wrong kind of value for this step.",
    "unknown_param_key": "A grouped parameter has a field this step doesn't use.",
    "invalid_node": "This step is missing information the pipeline needs to identify it.",
    "duplicate_node_id": "Two steps share the same identifier — each one needs its own.",
    "unresolved_shared_variable": "This step refers to a shared value that hasn't been defined.",
    "unknown_node_ref": "This refers to a step that no longer exists.",
    "invalid_edge_target": "A connection points to something that isn't a valid step.",
    "forward_from_source_file_invalid_chain": "This step passes through data from an input, but the app can't trace that path back to one.",
    "unused_input_node": "This input isn't used anywhere in the pipeline.",
    "missing_required_key": "The pipeline file is missing information it needs.",
    "yaml_syntax_error": "The pipeline file couldn't be read.",
    "top_level_not_mapping": "The pipeline file isn't structured the way the app expects.",
    "schema_version_invalid": "The pipeline file's version isn't recognized.",
    "schema_version_unsupported": "This pipeline file was made for a version this app doesn't support.",
    "block_not_mapping": "One of the steps in the pipeline file isn't structured correctly.",
    "input_bindings_unsupported": "This pipeline file uses an input format that isn't supported anymore.",
    "unknown_block_kind": "One of the steps in the pipeline file isn't a type the app recognizes.",
    "unknown_tool_slug": "One of the steps refers to a tool that doesn't exist.",
    "edge_not_mapping": "One of the connections in the pipeline file isn't structured correctly.",
    "edge_unknown_key": "One of the connections includes information the app doesn't recognize.",
    "edge_mapping_missing": "One of the connections is missing required information.",
    "edge_mapping_invalid": "One of the connections isn't set up correctly.",
    "edge_endpoint_missing": "One of the connections is missing one of its ends.",
    "edge_endpoint_not_scalar": "One of the connections points to more than one thing at once.",
    "params_by_block_invalid": "The parameters for one of the steps aren't formatted correctly.",
    "patch_empty_yaml": "The pipeline file is empty.",
    "patch_root_not_mapping": "The pipeline file isn't structured the way the app expects.",
    "patch_no_blocks": "The pipeline file doesn't contain any steps.",
    "patch_no_input_block": "The pipeline file doesn't have an input step to update.",
    "execution_not_mapping": "The pipeline's execution settings aren't in the expected form.",
    "execution_unknown_key": "The pipeline's execution settings include a setting that isn't recognised.",
    "execution_node_timeout_invalid": "The step time limit must be a whole number of seconds above zero.",
    "execution_node_timeout_above_ceiling": "The step time limit can't be longer than 72 hours."
  },
  "errorStage": {
    "sheet_enumeration_failed": "We couldn't read the sheets in this file.",
    "materialize_failed": "We couldn't convert this table.",
    "peek_failed": "We couldn't read this table.",
    "csv_row_wider_than_header": "A row has more values than the header. Make sure every row has the same number of columns as the header, then upload the file again.",
    "table_exceeds_memory_budget": "This table is too large to process. Split it into smaller files with fewer rows or columns, then upload them again.",
    "enrichment_llm_failed": "We couldn't analyze this table.",
    "schema_mismatch": "The table's columns didn't match what was expected.",
    "stalled": "Preparing this table stalled unexpectedly."
  },
  "error": {
    "generic": "Something went wrong. Please try again.",
    "invalid_scope_path": "That vertical isn't in the service menu. Pick a vertical from the menu.",
    "stageFailed": "This stage failed to load.",
    "network_error": "We couldn't reach the server. Check your connection and try again.",
    "request_timeout": "The request took too long. Please try again.",
    "stale_row_version": "This changed while you were editing. We've refreshed it — please review and try again.",
    "invalid_user_overrides": "Your changes couldn't be saved because they weren't in the expected format. Refresh the page and try again.",
    "dataset_not_ready": "This table isn't ready to confirm yet.",
    "enrich_in_progress": "This table is already being analyzed.",
    "dataset_already_uploaded": "This file has already been uploaded.",
    "upload_finalize_in_progress": "This file is still being registered. Wait a moment, then refresh to check whether it was added.",
    "upload_blob_properties_not_allowed": "This file was uploaded with storage settings we can't accept. Upload it again from this page.",
    "unsupported_source_format": "That file type isn't supported. Upload a .parquet, .csv, or .xlsx file.",
    "upload_failed": "The upload didn't complete. Please try again.",
    "upload_too_large": "\"{{filename}}\" is larger than the {{limit_mb}} MB limit for a single file.",
    "upload_size_mismatch": "The uploaded file's size didn't match what was expected, so it wasn't accepted. Please try uploading it again.",
    "empty_file": "The file \"{{filename}}\" is empty.",
    "workspace_not_found": "That workspace couldn't be found.",
    "dataset_not_found": "That table couldn't be found.",
    "case_not_found": "That test case couldn't be found.",
    "version_not_found": "That run couldn't be found.",
    "create_failed": "We couldn't create that. Please try again.",
    "delete_failed": "We couldn't delete that. Please try again.",
    "save_failed": "We couldn't save your changes. Please try again.",
    "confirm_failed": "We couldn't confirm this table. Please try again.",
    "reenrich_failed": "We couldn't re-analyze this table. Please try again.",
    "sample_failed": "We couldn't load the sample rows.",
    "sample_read_failed": "We couldn't preview this result. Please try again.",
    "sample_shape_invalid": "We couldn't preview this result. Please try again.",
    "catalog_load_failed": "We couldn't load your data catalog.",
    "workspace_load_failed": "We couldn't load this workspace.",
    "workspaces_load_failed": "We couldn't load your workspaces.",
    "capabilities_unavailable": "Test Ground is temporarily unavailable.",
    "turns_load_failed": "We couldn't load this conversation.",
    "turn_send_failed": "Your message couldn't be sent. Please try again.",
    "turn_cancel_failed": "We couldn't stop the current response.",
    "turn_in_progress": "The assistant is still responding. Please wait a moment.",
    "turn_not_cancellable": "That response has already finished.",
    "attach_too_large": "That file is over the 5 MB limit.",
    "attach_extract_failed": "Couldn't read that file — it may be damaged, or its contents couldn't be extracted.",
    "attach_unreadable_format": "This file doesn't contain text that can be read.",
    "attach_extract_unavailable": "The document reader is temporarily unavailable — please try again in a moment.",
    "invalid_request": "That file couldn't be attached. Please try again.",
    "runs_load_failed": "We couldn't load your runs.",
    "run_start_failed": "We couldn't start the run. Please try again.",
    "run_cancel_failed": "We couldn't cancel the run.",
    "run_in_progress": "A run is already in progress for this test case.",
    "version_cap_exceeded": "This test case has reached its run limit. Start a new test case to continue.",
    "version_already_terminal": "That run has already finished.",
    "pipeline_empty": "There's no pipeline to run yet. Design one in the Discover chat first.",
    "pipeline_invalid": "The pipeline has unresolved issues. Fix them before running.",
    "pipeline_config_yaml_invalid": "The pipeline couldn't be read. Please review it in Build.",
    "pipeline_load_failed": "We couldn't load your pipeline.",
    "pipeline_parse_failed": "We couldn't read this pipeline.",
    "issue_centre_failed": "We couldn't move the canvas to this item.",
    "issue_target_gone": "This item is no longer on the canvas.",
    "pipeline_yaml_unparseable": "This edit can't be read as a pipeline, so it wasn't saved. Undo the change or fix the structure.",
    "scope_vocabulary_unavailable": "The service menu needed to validate the vertical is temporarily unavailable. Try again shortly.",
    "structural_pending": "Saving your change. Wait for it to finish before making another.",
    "structural_save_unconfirmed": "Still saving your change. It isn't confirmed yet — wait for it to finish.",
    "draft_locked": "The assistant is updating this pipeline. Try again once it finishes.",
    "functions_load_failed": "We couldn't load the function palette.",
    "reggie_write_forbidden": "You can’t change this test workspace. Ask the model’s owner or an administrator to give you the Collaborator permission on the model’s Access tab.",
    "reggie_sso_required": "Sign in with your organization account to use Test Ground.",
    "reggie_upstream_unreachable": "Test Ground is temporarily unreachable. Please try again in a moment.",
    "webhook_url_invalid": "The webhook URL must be an absolute https:// address that is reachable from the internet.",
    "webhook_url_unverified": "We couldn't check that webhook address right now. Try saving again in a moment.",
    "input_source_not_owned": "This pipeline's input file isn't available to your account. Attach the source data again, then run.",
    "speech_disabled": "Voice input is unavailable.",
    "speech_rate_limited": "Too many voice messages. Wait a moment and try again.",
    "speech_audio_invalid": "The recording could not be read. Please record your message again.",
    "speech_no_speech": "No speech was heard. Please try again.",
    "speech_upstream_failed": "Your speech could not be transcribed. Please try again.",
    "speech_upstream_timeout": "Transcription took too long. Please try again.",
    "speech_unsupported": "Voice input is not supported in this browser.",
    "speech_mic_denied": "Microphone access was denied. Allow access in your browser settings and try again.",
    "speech_mic_unavailable": "No microphone was found. Connect one and try again.",
    "speech_mic_busy": "The microphone could not be opened. Check whether another app is using it.",
    "speech_cancelled": "Voice input cancelled.",
    "speech_composer_disabled": "Voice input stopped because chat is busy. Your draft is unchanged.",
    "speech_request_timeout": "Transcription took too long. Please try again.",
    "speech_request_failed": "Voice input failed. Please try again.",
    "speech_send_failed": "Your voice message could not be sent. Please try again.",
    "request_body_too_large": "That request is too large."
  }
}
